import Box from "@cloudscape-design/components/box"
import Container from "@cloudscape-design/components/container"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useEffect, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import {
  CompareContent,
  type DocContent,
} from "../../components/CompareVersionsModal/CompareVersionsModal"
import Breadcrumb from "../../components/Navigation/Breadcrumb"
import { getDTADisplayName } from "../../components/shared/dtaDisplay"
import { useDtaDetail, useDtaVersion, useDtaVersions } from "../../hooks/queries/useDtas"

export default function CompareVersionsPage() {
  const { dtaId } = useParams<{ dtaId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [fromVer, setFromVer] = useState(searchParams.get("from") || "")
  const [toVer, setToVer] = useState(searchParams.get("to") || "")

  const { data: dta } = useDtaDetail(dtaId)
  const { data: versions = [] } = useDtaVersions(dtaId)

  // Default the compared versions to the two most recent once the version list loads.
  useEffect(() => {
    if (!fromVer && !toVer && versions.length >= 2) {
      setFromVer(versions[1].version)
      setToVer(versions[0].version)
    }
  }, [versions, fromVer, toVer])

  // Reflect the selected versions in the URL (replace, so back-button history stays clean).
  useEffect(() => {
    if (!fromVer || !toVer || fromVer === toVer) return
    setSearchParams(
      (prev) => {
        if (prev.get("from") === fromVer && prev.get("to") === toVer) return prev
        const params = new URLSearchParams(prev)
        params.set("from", fromVer)
        params.set("to", toVer)
        return params
      },
      { replace: true },
    )
  }, [fromVer, toVer, setSearchParams])

  const comparing = !!fromVer && !!toVer && fromVer !== toVer
  const fromQuery = useDtaVersion(dtaId, comparing ? fromVer : undefined)
  const toQuery = useDtaVersion(dtaId, comparing ? toVer : undefined)
  const loading = comparing && (fromQuery.isLoading || toQuery.isLoading)
  const fromContent = (fromQuery.data?.document_content as DocContent) || null
  const toContent = (toQuery.data?.document_content as DocContent) || null

  const scrolledRef = useRef(false)
  const itemToHighlight = searchParams.get("item")

  useEffect(() => {
    if (!itemToHighlight || scrolledRef.current || loading) return
    const scroll = (els: NodeListOf<Element>) => {
      scrolledRef.current = true
      els[0].scrollIntoView({ block: "center" })
      els.forEach((e) => {
        const el = e as HTMLElement
        el.style.outline = "3px solid rgba(59, 130, 246, 0.7)"
        el.style.outlineOffset = "-3px"
        el.style.borderRadius = "8px"
        el.style.transition = "outline-color 1.5s ease-in-out"
        setTimeout(() => {
          el.style.outline = ""
          el.style.outlineOffset = ""
          el.style.transition = ""
        }, 1500)
      })
    }
    const observer = new MutationObserver(() => {
      const els = document.querySelectorAll(`[data-diff-item="${itemToHighlight}"]`)
      if (els.length) {
        observer.disconnect()
        scroll(els)
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    const els = document.querySelectorAll(`[data-diff-item="${itemToHighlight}"]`)
    if (els.length) {
      observer.disconnect()
      scroll(els)
    }
    return () => observer.disconnect()
  }, [itemToHighlight, loading])

  const versionOptions = versions.map((v) => ({
    value: v.version,
    label: `v${v.version}`,
  }))

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <SpaceBetween size="xs">
          <Breadcrumb
            items={[
              { label: "All DTAs", path: "/dtas" },
              { label: dta?.study_id || "", path: `/studies/${dta?.study_id}` },
              { label: dta ? getDTADisplayName(dta) : "", path: `/dtas/${dtaId}` },
              { label: "Compare" },
            ]}
          />
          {dta && (
            <Header variant="h1">
              Comparing {dta.study_id} &mdash; {dta.vendor_name} &mdash; {dta.domains?.join(", ")}
            </Header>
          )}
        </SpaceBetween>

        <Container>
          <SpaceBetween size="l">
            <SpaceBetween direction="horizontal" size="m" alignItems="end">
              <FormField label="From">
                <Select
                  selectedOption={fromVer ? { value: fromVer, label: `v${fromVer}` } : null}
                  onChange={({ detail }) => setFromVer(detail.selectedOption.value ?? "")}
                  options={versionOptions}
                  placeholder="Select"
                />
              </FormField>
              <Box padding={{ bottom: "xs" }}>
                <Icon name="arrow-right" size="medium" />
              </Box>
              <FormField label="To">
                <Select
                  selectedOption={toVer ? { value: toVer, label: `v${toVer}` } : null}
                  onChange={({ detail }) => setToVer(detail.selectedOption.value ?? "")}
                  options={versionOptions}
                  placeholder="Select"
                />
              </FormField>
            </SpaceBetween>

            <CompareContent
              loading={loading}
              fromVer={fromVer}
              toVer={toVer}
              fromContent={fromContent}
              toContent={toContent}
              highlightSection={searchParams.get("section") || undefined}
              highlightItem={searchParams.get("item") || undefined}
            />
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Box>
  )
}
